import { Component } from '@theme/component';

/**
 * Drives the Reviews carousel.
 *
 * The track is a native scroll-snap container, so swiping, trackpad scrolling
 * and keyboard scrolling all work with no JS. This adds the arrows and keeps
 * their state in sync.
 *
 * Two things learned the hard way and encoded here:
 *
 *  - `scroll-snap-type: mandatory` cancels programmatic *smooth* scrolls, and
 *    smooth scrolling can be throttled entirely (background tabs, reduced
 *    motion, some embedded browsers). So the snap is lifted for the duration
 *    and a settle timer asserts the final position outright - the carousel can
 *    never silently fail to move.
 *  - the index is updated when a button is pressed rather than waiting for the
 *    IntersectionObserver, which otherwise leaves the arrows advancing from a
 *    stale position. The observer still handles manual swipes.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @extends {Component<Refs>}
 */
export class ReviewsCarouselComponent extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #settleTimer;

  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) continue;
          const index = slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (index === -1) continue;
          this.#index = index;
          this.#syncControls();
        }
      },
      { root: this.refs.track, threshold: [0.6] }
    );

    slides.forEach((slide) => this.#observer?.observe(slide));
    this.#syncControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    clearTimeout(this.#settleTimer);
  }

  /**
   * How many cards are visible, so the arrows stop at the last full page
   * rather than scrolling into empty space.
   * @returns {number}
   */
  #perView() {
    const slides = this.refs.slides ?? [];
    const slideWidth = slides[0]?.getBoundingClientRect().width ?? 0;
    if (!slideWidth) return 1;
    return Math.max(1, Math.round(this.refs.track.clientWidth / slideWidth));
  }

  #maxIndex() {
    return Math.max(0, (this.refs.slides ?? []).length - this.#perView());
  }

  #syncControls() {
    const { prev, next } = this.refs;
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= this.#maxIndex();
  }

  /** @param {number} index */
  #goTo(index) {
    const slides = this.refs.slides ?? [];
    const clamped = Math.max(0, Math.min(this.#maxIndex(), index));
    const target = slides[clamped];
    if (!target) return;

    const track = this.refs.track;
    const to = target.offsetLeft - track.offsetLeft;

    this.#index = clamped;
    this.#syncControls();

    track.style.scrollSnapType = 'none';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      track.scrollLeft = to;
      track.style.scrollSnapType = '';
      return;
    }

    track.scrollTo({ left: to, behavior: 'smooth' });

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      if (Math.abs(track.scrollLeft - to) > 2) track.scrollLeft = to;
      track.style.scrollSnapType = '';
    }, 550);
  }

  /** Fires from the previous arrow. */
  previous() {
    this.#goTo(this.#index - 1);
  }

  /** Fires from the next arrow. */
  next() {
    this.#goTo(this.#index + 1);
  }
}

if (!customElements.get('reviews-carousel-component')) {
  customElements.define('reviews-carousel-component', ReviewsCarouselComponent);
}
